import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, ArrowRight, CalendarDays, Clock, MapPin,
  Gamepad2, UserCircle, BookOpen, Target
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const [regStatus, setRegStatus] = useState({ is_open: false, competition: null });

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/registration/status`);
      setRegStatus(res.data);
    } catch {
      setRegStatus({ is_open: false, competition: null });
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const i = setInterval(fetchStatus, 5000);
    return () => clearInterval(i);
  }, [fetchStatus]);

  const comp = regStatus.competition;
  const isOpen = regStatus.is_open && comp;
  const compName = comp?.name || 'Brain Gym Jr. Olympiad';

  return (
    <div className="min-h-screen bg-[#fffdf5] flex flex-col" data-testid="landing-page">
      {/* Top Bar */}
      <header className="px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Brain Gym Jr." className="w-10 h-10 sm:w-11 sm:h-11 object-contain" />
          <div>
            <h1 className="text-sm sm:text-base font-bold text-[#0f172a] leading-tight" style={{ fontFamily: 'Fredoka, sans-serif' }}>Brain Gym Jr.</h1>
            <p className="text-[10px] sm:text-xs text-[#64748b] -mt-0.5">Olympiad</p>
          </div>
        </div>
      </header>

      {/* Hero */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-8 sm:py-16">
        <section className="max-w-2xl w-full text-center animate-fade-in-up" data-testid="landing-hero">
          {/* Status pill */}
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold mb-6 sm:mb-7 ${isOpen ? 'bg-[#dcfce7] text-[#16a34a]' : 'bg-[#f1f5f9] text-[#64748b]'}`}>
            <span className={`w-2 h-2 rounded-full ${isOpen ? 'bg-[#22c55e] animate-pulse' : 'bg-[#94a3b8]'}`} />
            {isOpen ? 'Registration Open!' : 'No Active Competition'}
          </div>

          {/* Greeting */}
          <p className="text-xs sm:text-sm text-[#2565eb] font-bold mb-3 tracking-[0.2em] uppercase" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Hey Brainiacs!
          </p>

          {/* Competition name */}
          <h2
            className="text-3xl sm:text-5xl lg:text-6xl font-bold text-[#0f172a] mb-5 sm:mb-6 leading-[1.05] px-2"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
            data-testid="landing-competition-name"
          >
            {compName}
          </h2>

          {isOpen ? (
            <>
              <p className="text-sm sm:text-base text-[#64748b] mb-7 sm:mb-9 max-w-md mx-auto px-2">
                8 exciting rounds. Real-time leaderboards. Awesome prizes.
              </p>

              {/* Event chips */}
              <div className="flex flex-wrap items-center justify-center gap-2 mb-8 sm:mb-10 px-2" data-testid="landing-event-chips">
                {comp.eventDate && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#eff6ff] text-[#2565eb] text-xs font-bold">
                    <CalendarDays className="w-3.5 h-3.5" /> {comp.eventDate}
                  </span>
                )}
                {comp.eventStartTime && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#fef3c7] text-[#d97706] text-xs font-bold">
                    <Clock className="w-3.5 h-3.5" /> {comp.eventStartTime}
                  </span>
                )}
                {comp.venue && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#fdf2f8] text-[#be185d] text-xs font-bold">
                    <MapPin className="w-3.5 h-3.5" /> {comp.venue}
                  </span>
                )}
                {comp.ageGroup && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#dcfce7] text-[#16a34a] text-xs font-bold">
                    <Target className="w-3.5 h-3.5" /> Ages {comp.ageGroup}
                  </span>
                )}
              </div>

              {/* Big CTA */}
              <button
                onClick={() => navigate('/registration')}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-[#face39] text-[#0f172a] font-bold text-base sm:text-lg shadow-[0_5px_0_#d97706] active:translate-y-1 active:shadow-[0_2px_0_#d97706] hover:bg-[#fde047] transition-all"
                data-testid="landing-register-btn"
              >
                <Sparkles className="w-5 h-5" />
                Register
                <ArrowRight className="w-5 h-5" />
              </button>

              <p className="text-xs text-[#94a3b8] mt-3">Takes less than 2 minutes</p>
            </>
          ) : (
            <div data-testid="landing-no-event-state">
              <p className="text-sm sm:text-base text-[#64748b] mb-7 sm:mb-9 max-w-md mx-auto px-2">
                No competition is scheduled right now. Check back soon — we'll announce the next Olympiad here.
              </p>
              <button
                disabled
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-[#e2e8f0] text-[#94a3b8] font-bold cursor-not-allowed text-base sm:text-lg"
                data-testid="landing-register-btn-disabled"
              >
                <Sparkles className="w-5 h-5" />
                Register
              </button>
            </div>
          )}
        </section>

        {/* Compact returning-user pills */}
        <section className="mt-12 sm:mt-16 w-full max-w-md animate-fade-in-up stagger-2">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#94a3b8] font-bold text-center mb-3">Already Registered?</p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={() => navigate('/practice')}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-[#e2e8f0] hover:border-[#2565eb] hover:bg-[#eff6ff] text-xs font-semibold text-[#475569] transition-all"
              data-testid="landing-practice-zone-btn"
            >
              <BookOpen className="w-3.5 h-3.5 text-[#2565eb]" /> Practice Zone
            </button>
            <button
              onClick={() => navigate('/lobby')}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-[#e2e8f0] hover:border-[#d97706] hover:bg-[#fef3c7] text-xs font-semibold text-[#475569] transition-all"
              data-testid="landing-live-event-btn"
            >
              <Gamepad2 className="w-3.5 h-3.5 text-[#d97706]" /> Join the Live Event
            </button>
            <button
              onClick={() => navigate('/parent')}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-[#e2e8f0] hover:border-[#be185d] hover:bg-[#fdf2f8] text-xs font-semibold text-[#475569] transition-all"
              data-testid="landing-parent-portal-btn"
            >
              <UserCircle className="w-3.5 h-3.5 text-[#be185d]" /> Parent Portal
            </button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="px-4 sm:px-6 py-4 sm:py-5 border-t border-[#e2e8f0] flex items-center justify-between text-xs text-[#94a3b8]">
        <p>© Brain Gym Jr. Olympiad</p>
        <button
          onClick={() => navigate('/admin/login')}
          className="hover:text-[#2565eb] transition-colors"
          data-testid="landing-footer-admin-link"
        >
          Admin
        </button>
      </footer>
    </div>
  );
};

export default LandingPage;
